import React, { useState } from 'react';

import Checkbox from '~/components/ui/atoms/Checkbox';
import type {
  PromotionPreviewResultPromotionNewMember,
  PromotionPreviewResultPromotionNewMembers,
} from '~/server_shared/openapi/data-contracts';

import { type NewMemberKind, relatedChangesForMember } from './newMembers';
import { objectLabel } from './promotionsUtils';
import type { PromotionChange } from './types';
import { useScopeValueLabel } from './useScopeValueLabel';

interface MemberRowProps {
  kind: NewMemberKind;
  member: PromotionPreviewResultPromotionNewMember;
  label: string;
  checked: boolean;
  relatedChanges: PromotionChange[];
  targetName: string;
  onToggleMember: (kind: NewMemberKind, externalId: string, checked: boolean) => void;
}

const MemberRow: React.FC<MemberRowProps> = ({
  kind,
  member,
  label,
  checked,
  relatedChanges,
  targetName,
  onToggleMember,
}) => {
  const [expanded, setExpanded] = useState(false);
  const relatedCount = relatedChanges.length;

  return (
    <div className="flex flex-col px-12 py-8">
      <div className="flex items-center justify-between gap-x-12">
        <Checkbox
          size="xs"
          label={label}
          checked={checked}
          onChange={(_e, isChecked) => onToggleMember(kind, member.external_id, isChecked)}
        />
        {relatedCount > 0 && (
          <button
            type="button"
            className="flex cursor-pointer items-center gap-x-4 border-0 bg-transparent p-0 text-slate-900 underline decoration-dotted underline-offset-4"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            related to {relatedCount} change{relatedCount === 1 ? '' : 's'}
            <span aria-hidden>{expanded ? '⌃' : '⌄'}</span>
          </button>
        )}
      </div>

      {expanded && relatedCount > 0 && (
        <ul className="my-4 ml-32 flex list-disc flex-col gap-y-2 text-slate-900">
          {relatedChanges.map((change) => (
            <li key={change.change_id}>{objectLabel(change)}</li>
          ))}
        </ul>
      )}

      {!checked && (
        <div className="ml-32 mt-4 text-orange-700" role="alert">
          ⚠ {label} won’t be created on {targetName}.
          {relatedCount > 0
            ? ` Its ${relatedCount} related change${relatedCount === 1 ? '' : 's'} will be pruned or dropped from this promotion.`
            : ' No selected changes depend on it.'}
        </div>
      )}
    </div>
  );
};

interface MemberSectionProps {
  title: string;
  kind: NewMemberKind;
  members: PromotionPreviewResultPromotionNewMember[];
  promotedIds: Set<string>;
  changes: PromotionChange[];
  targetName: string;
  onToggleMember: (kind: NewMemberKind, externalId: string, checked: boolean) => void;
}

const MemberSection: React.FC<MemberSectionProps> = ({
  title,
  kind,
  members,
  promotedIds,
  changes,
  targetName,
  onToggleMember,
}) => {
  // Resolve the LOB label through the shared helper (as the change selector does one screen later) rather than
  // the raw-code `display_name` the backend supplies for a new LOB — `useLobConfiguration` resolves even a LOB
  // the target doesn't support yet. A new sub-org keeps its server-supplied `display_name` (`after_value.name`),
  // which the resolver can't recover for a member the target doesn't have.
  const scopeValueLabel = useScopeValueLabel();
  const labelFor = (member: PromotionPreviewResultPromotionNewMember): string =>
    kind === 'lob' ? scopeValueLabel('lobs', member.external_id) : member.display_name || member.external_id;

  return (
    <div className="flex flex-col gap-y-4">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-900">{title}</span>
      <div className="flex flex-col divide-y divide-slate-200 rounded-md border border-solid border-slate-300">
        {members.map((member) => (
          <MemberRow
            key={member.external_id}
            kind={kind}
            member={member}
            label={labelFor(member)}
            checked={promotedIds.has(member.external_id)}
            relatedChanges={relatedChangesForMember(changes, kind, member.external_id)}
            targetName={targetName}
            onToggleMember={onToggleMember}
          />
        ))}
      </div>
    </div>
  );
};

interface PromotionNewMembersStepProps {
  /** The new LOB / sub-org members this promotion's diff would create (from `PromotionPreviewResult`). */
  newMembers: PromotionPreviewResultPromotionNewMembers;
  /** The previewed changes — each member row derives its "related to N changes" blast radius from their
   * scope attribution (data the preview already carries; no extra request). */
  changes: PromotionChange[];
  /** Human name of the promotion target, anchoring the copy ("don't exist on <target> yet"). */
  targetName?: string;
  /** The currently-promoted LOB / sub-org ids (default: all checked — promote-all). */
  promotedLobIds: Set<string>;
  promotedSubOrgIds: Set<string>;
  /** Toggle a member's promote decision. */
  onToggleMember: (kind: NewMemberKind, externalId: string, checked: boolean) => void;
}

/** Interim opt-in step for a one-shot promotion that creates new LOBs / sub-orgs: lists the members the diff
 * would create (all checked by default), each with the changes related to it, so the operator decides with the
 * blast radius visible. Unchecking one drops its CREATE and prunes/drops its dependent changes (via the refine
 * re-projection) — dropped changes then simply don't appear in the review step, which is why the consequence is
 * surfaced HERE, at decision time. The decision is locked onto the corresponding rows in the change list.
 * Rendered only when the preview carries new members. */
const PromotionNewMembersStep: React.FC<PromotionNewMembersStepProps> = ({
  newMembers,
  changes,
  targetName,
  promotedLobIds,
  promotedSubOrgIds,
  onToggleMember,
}) => {
  const lobs = newMembers.lobs ?? [];
  const subOrganizations = newMembers.sub_organizations ?? [];
  const target = targetName || 'the target';

  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-y-4">
        <span className="text-xs uppercase tracking-wide text-slate-900">Step 1 of 2</span>
        <span className="font-bold">New LOBs & sub-organizations</span>
        <span className="text-slate-900">
          These don’t exist on {target} yet. Unchecking one leaves it out and prunes every change in this promotion that
          depends on it.
        </span>
      </div>

      {lobs.length > 0 ? (
        <MemberSection
          title="Lines of business"
          kind="lob"
          members={lobs}
          promotedIds={promotedLobIds}
          changes={changes}
          targetName={target}
          onToggleMember={onToggleMember}
        />
      ) : null}

      {subOrganizations.length > 0 ? (
        <MemberSection
          title="Sub-organizations"
          kind="sub_organization"
          members={subOrganizations}
          promotedIds={promotedSubOrgIds}
          changes={changes}
          targetName={target}
          onToggleMember={onToggleMember}
        />
      ) : null}
    </div>
  );
};

export default PromotionNewMembersStep;
